// The table-cell status pill for rows a Stimulus controller builds or updates
// client-side. It must stay identical to the server-rendered cell
// (BadgeHelper#status_badge(text, tone, dot: true)) and to React's
// StatusBadgeCell.tsx: `gl-status-badge gl-status-badge--<tone>` with a
// leading `gl-status-dot` — HAML and React tables look the same (#15126 /
// #15172).
//
// The class string and the raw-status slug rule are the Stimulus twin of
// app/frontend/react/lib/statusBadge.ts (statusBadgeClassName / statusSlug);
// statusBadge.parity.test.ts pins the two to the same output.
import { escapeHtml } from "./html_sanitizer"

const DOT_HTML = '<span class="gl-status-dot" aria-hidden="true"></span>'
const MODIFIER_PREFIX = "gl-status-badge--"
// Modifiers that set a pill's size or canvas, not its tone. A re-tone keeps
// them; every other `gl-status-badge--*` class is a tone.
const NON_TONE_MODIFIERS = new Set(["sm", "md", "quiet", "on-dark", "on-primary"])

// A raw status (`in_cart` / `In Cart`) → its modifier slug (`in-cart`).
export function statusSlug(status) {
  return String(status).toLowerCase().replace(/[\s_]+/g, "-")
}

// `size: "sm"` puts the size modifier BEFORE the tone
// (`gl-status-badge gl-status-badge--sm gl-status-badge--<tone>`), the order the
// compact pills have always been built in. Without it the string is unchanged.
export function statusBadgeClass(tone, { size } = {}) {
  const sizeClass = size ? ` ${MODIFIER_PREFIX}${size}` : ""
  return `gl-status-badge${sizeClass} ${MODIFIER_PREFIX}${tone}`
}

export function statusBadgeHtml(text, tone) {
  return `<span class="${statusBadgeClass(escapeHtml(tone))}">${DOT_HTML}${escapeHtml(text)}</span>`
}

// Re-tone an existing pill in place. Setting textContent alone would drop the
// dot, so the dot and label are rebuilt together.
export function setStatusBadge(element, text, tone) {
  element.className = statusBadgeClass(tone)
  element.innerHTML = `${DOT_HTML}${escapeHtml(text)}`
}

// Re-tone a pill the server already rendered: drop every tone modifier and add
// `gl-status-badge--<tone>` last. Nothing else changes — no dot, no label, and
// size / canvas modifiers (NON_TONE_MODIFIERS) and any other class stay where
// they are. Use setStatusBadge instead for a pill whose label changes with it.
export function retoneStatusBadge(element, tone) {
  Array.from(element.classList)
    .filter((name) => name.startsWith(MODIFIER_PREFIX) && !NON_TONE_MODIFIERS.has(name.slice(MODIFIER_PREFIX.length)))
    .forEach((name) => element.classList.remove(name))
  element.classList.add(`${MODIFIER_PREFIX}${tone}`)
}
